import React, { useState, useRef, useEffect } from "react"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar"
import { Badge } from "@/components/ui/badge"
import { LogOut, User, Calendar, MapPin, Mail, ChevronDown } from "lucide-react"
import { useAuth } from "../contexts/auth-context"
import { useTranslation } from "../contexts/translation-context"

export function UserProfileDropdown() {
  const { t } = useTranslation()
  const { user, logout, isAuthenticated } = useAuth()
  const [isOpen, setIsOpen] = useState(false)
  const [isLoggingOut, setIsLoggingOut] = useState(false)
  const [avatarError, setAvatarError] = useState(false)
  const dropdownRef = useRef<HTMLDivElement>(null)

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false)
      }
    }

    document.addEventListener("mousedown", handleClickOutside)
    return () => {
      document.removeEventListener("mousedown", handleClickOutside)
    }
  }, [])

  // Reset avatar error when user changes
  useEffect(() => {
    setAvatarError(false)
  }, [user?.avatarUrl])

  if (!isAuthenticated || !user) {
    return null
  }

  const handleLogout = async () => {
    setIsLoggingOut(true)
    await logout()
    setIsLoggingOut(false)
    setIsOpen(false)
  }

  const formatDate = (dateString?: string | Date) => {
    if (!dateString) return t("auth.notProvided")
    return new Date(dateString).toLocaleDateString()
  }

  // Get provider display name
  const getProviderName = (provider?: string) => {
    if (!provider) return "Platform"

    switch (provider) {
      case "google":
        return "Google"
      case "facebook":
        return "Facebook"
      case "email":
        return "Email"
      default:
        return provider.charAt(0).toUpperCase() + provider.slice(1)
    }
  }

  const handleAvatarError = () => {
    console.warn("Avatar failed to load:", user.avatarUrl)
    setAvatarError(true)
  }

  // Use fallback if avatar failed to load or if no avatarUrl
  const avatarSrc = avatarError || !user.avatarUrl ? "/placeholder.svg" : user.avatarUrl

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Trigger Button */}
      <Button variant="outline" size="sm" onClick={() => setIsOpen(!isOpen)} className="flex items-center gap-2">
        <Avatar className="w-6 h-6">
          <AvatarImage
            src={avatarSrc || "/placeholder.svg"}
            alt={user.name}
            onError={handleAvatarError}
            referrerPolicy="no-referrer"
          />
          <AvatarFallback className="text-xs">
            {user.name
              .split(" ")
              .map((n) => n[0])
              .join("")
              .toUpperCase()}
          </AvatarFallback>
        </Avatar>
        <span className="hidden sm:inline">{user.name.split(" ")[0]}</span>
        <ChevronDown className={`w-4 h-4 transition-transform ${isOpen ? "rotate-180" : ""}`} />
      </Button>

      {/* Dropdown Content */}
      {isOpen && (
        <div className="absolute right-0 top-full mt-2 z-50">
          <Card className="w-80 shadow-lg border">
            <CardHeader className="text-center pb-3">
              <Avatar className="w-16 h-16 mx-auto mb-3">
                <AvatarImage
                  src={avatarSrc || "/placeholder.svg"}
                  alt={user.name}
                  onError={handleAvatarError}
                  referrerPolicy="no-referrer"
                />
                <AvatarFallback className="text-lg">
                  {user.name
                    .split(" ")
                    .map((n) => n[0])
                    .join("")
                    .toUpperCase()}
                </AvatarFallback>
              </Avatar>
              <CardTitle className="text-lg">{user.name}</CardTitle>
              <CardDescription>
                <Badge variant="secondary" className="bg-secondary-50 text-secondary border-secondary">
                  {getProviderName(user.provider)} {t("profile.user")}
                </Badge>
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-3">
                <div className="flex items-center gap-3 text-sm">
                  <Mail className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                  <span className="truncate">{user.email}</span>
                </div>
                {user.birthday && (
                  <div className="flex items-center gap-3 text-sm">
                    <Calendar className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                    <span>{formatDate(user.birthday)}</span>
                  </div>
                )}
                {user.hometown && (
                  <div className="flex items-center gap-3 text-sm">
                    <MapPin className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                    <span className="truncate">{user.hometown}</span>
                  </div>
                )}
                <div className="flex items-center gap-3 text-sm">
                  <User className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                  <span>
                    {t("profile.memberSince")} {formatDate(user.createdAt)}
                  </span>
                </div>
              </div>

              <Button onClick={handleLogout} variant="outline" className="w-full" disabled={isLoggingOut}>
                <LogOut className="w-4 h-4 mr-2" />
                {isLoggingOut ? t("profile.signingOut") : t("profile.signOut")}
              </Button>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  )
}
